import { CursorClientPos, RenderedImageRect, RenderedImageSizeInfo } from './types';

declare let globals: any;

/**
 * Easy to use selector for finding elements inside and outside of shadowdom
 * https://stackoverflow.com/questions/29629492/how-to-query-elements-within-shadow-dom-from-outside-in-dart
 */

export function $$$(selector, rootNode = document.body) {
  const arr = [];

  const traverser = node => {
    // 1. decline all nodes that are not elements
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return;
    }

    // 2. add the node to the array, if it matches the selector
    if (node.matches(selector)) {
      arr.push(node);
    }

    // 3. loop through the children
    const children = node.children;
    if (children.length) {
      for (const child of children) {
        traverser(child);
      }
    }

    // 4. check for shadow DOM, and loop through it's children
    const shadowRoot = node.shadowRoot;
    if (shadowRoot) {
      const shadowChildren = shadowRoot.children;
      for (const shadowChild of shadowChildren) {
        traverser(shadowChild);
      }
    }
  };

  traverser(rootNode);

  return arr;
}

export const DEFAULT_DEBOUNCE_DURATION = 500;

/** Debounces the specified function and returns a wrapper function */
export function debounce(method, duration = DEFAULT_DEBOUNCE_DURATION) {
  let timeoutId;

  function debounceWrapper(...args) {
    debounceWrapper.clear();

    timeoutId = setTimeout(() => {
      timeoutId = null;
      method.apply(this, args);
    }, duration);
  }

  debounceWrapper.clear = function () {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  return debounceWrapper;
}

export function isCursorOverRenderedImage(img: HTMLImageElement, cursor: CursorClientPos): boolean {
  const renderedImgRect = getRenderedImageRect(img);

  return cursor.clientX >= renderedImgRect.left && cursor.clientX <= renderedImgRect.right && cursor.clientY >= renderedImgRect.top && cursor.clientY <= renderedImgRect.bottom;
}

export function getRenderedImageRect(img: HTMLImageElement): RenderedImageRect {
  const sizeInfo = getRenderedImageSizeInfo(img);
  const boundingClientRect = img.getBoundingClientRect();

  return {
    bottom: boundingClientRect.bottom,
    height: boundingClientRect.height,
    top: boundingClientRect.top,
    y: boundingClientRect.y,

    width: sizeInfo.width,
    left: boundingClientRect.left + sizeInfo.left,
    x: boundingClientRect.left + sizeInfo.left,
    right: boundingClientRect.right - sizeInfo.left, // right padding should be the same as left padding
  };
}

/**
 * Returns the size and position of the drawn image in an `img` element,
 *   that has `object-fit: contain;` style.
 * @see https://stackoverflow.com/a/37270863/4112200
 */
export function getRenderedImageSizeInfo(img: HTMLImageElement): RenderedImageSizeInfo {
  const pos = window.getComputedStyle(img).getPropertyValue('object-position').split(' ');
  return getRenderedSize(true, img.width, img.height, img.naturalWidth, img.naturalHeight, parseInt(pos[0]));

  function getRenderedSize(contains, cWidth, cHeight, width, height, pos) {
    var oRatio = width / height,
      cRatio = cWidth / cHeight;
    return function () {
      if (contains ? oRatio > cRatio : oRatio < cRatio) {
        this.width = cWidth;
        this.height = cWidth / oRatio;
      } else {
        this.width = cHeight * oRatio;
        this.height = cHeight;
      }
      this.left = (cWidth - this.width) * (pos / 100);
      this.right = this.width + this.left;
      return this;
    }.call({});
  }
}

/**
 * Converts encoded html entities string like &lt;lilu-p slot=&#39; to <lilu-p>
 * @param entities
 * @returns decoded string
 */
export function decodeHTMLEntities(entities: string) {
  let textArea = document.createElement('textarea');
  textArea.innerHTML = entities;
  return textArea.value;
}

/**
 * Creates element CSS variables to be used in the components to set the font-size by breakpoint
 * @param element
 * @param size
 * @returns void
 */

export function createCSSVariablesForFontsizes(element: HTMLElement, sizesString: string) {
  const sizePairs = sizesString.split(' ');

  for (const sizeLabel of sizePairs) {
    const sizeInfo = sizeLabel.split('-');

    let prefix = '';
    let sizePx: number | undefined;

    if (sizeInfo.length === 3) {
      prefix = '-' + sizeInfo[0];
      sizePx = parseInt(sizeInfo[2], 10);
    } else if (sizeInfo.length === 2) {
      sizePx = parseInt(sizeInfo[1], 10);
    }

    if (!isNaN(sizePx)) {
      element.style?.setProperty(`--selected-font-size${prefix}`, `${sizePx}px`);
    }
  }
}

export function geti18n(key: string): string {
  const translations = globals?.translations;
  return translations?.[key] ?? key;
}
